<template>
  <div style="width: 100%; height: 300px;">
    <v-chart
      ref="chartRef"
      class="chart-container"
      :option="chartOptions"
      autoresize
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import VChart from 'vue-echarts'
import { markLineFor } from '@/zones/zoneMarkers'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatNumber } from '@/i18n/format'
import { use } from 'echarts/core'
import { MarkLineComponent } from 'echarts/components'

use([MarkLineComponent])

// Tripwire crossings chart — two series (Inbound + Outbound) over time.
// Accepts the per-zone tripwire payload shape: { "YYYY-MM-DD HH:MM:SS"|"YYYY-MM-DD":
// { zone_in_count, zone_out_count } }. Unlike polygon counts it is FLAT per
// timestamp (no nested zone_name layer), because the per-zone analytics blob
// is already scoped to a single zone.
const props = defineProps({
  chartData: Object,
  timeSpan: String,
  // Context-aware labels: Entrance/CEE → "Entries / Wrong way";
  // Exit → "Exits / Wrong way"; everything else → "Inbound / Outbound".
  // Defaults to the generic neutral pair.
  // Empty → the translated 'Inbound' / 'Outbound'.
  inLabel: { type: String, default: '' },
  outLabel: { type: String, default: '' },
  // Optional weather overlay — OFF by default (see ChartLineTotalCountsDetectionZone).
  // `weatherByDate` maps each timestamp/date key → daytime temperature (°C); drawn
  // as a cold→hot colour-graded line on a right-hand axis.
  showWeather: { type: Boolean, default: false },
  weatherByDate: { type: Object, default: null },
  // "Something changed at this spot" days (GET /zone_markers/): a dashed line each.
  markers: { type: Array, default: () => [] },
})

const chartRef = ref(null)

const { t } = useI18n()
// Dutch numbers (1.234 · 12,5) for tooltips and the value axis; English keeps
// ECharts' own formatting, so these are only set when the portal isn't English.
const nlNum = v => formatNumber(v, { decimals: Math.min(2, (String(v).split('.')[1] || '').length) })

const chartOptions = computed(() => {
  const dates = []
  const inCounts = []
  const outCounts = []

  if (props.chartData) {
    // Sort keys so the X-axis is chronological regardless of dict insertion order.
    const sortedKeys = Object.keys(props.chartData).sort()
    for (const ts of sortedKeys) {
      const row = props.chartData[ts] || {}
      dates.push(ts)
      inCounts.push(row.zone_in_count ?? 0)
      outCounts.push(row.zone_out_count ?? 0)
    }
  }

  const showWx = !!(props.showWeather && props.weatherByDate)
  const temps = showWx ? dates.map(d => props.weatherByDate[d] ?? null) : []
  const tVals = temps.filter(t => t != null)
  const tMin = tVals.length ? Math.min(...tVals) : 0
  const tMax = tVals.length ? Math.max(...tVals) : 30
  const tempLabel = t('charts.line.temperature')
  const inLabel = props.inLabel || t('charts.line.inbound')
  const outLabel = props.outLabel || t('charts.line.outbound')

  return {
    tooltip: currentLanguage() === 'en' ? { trigger: 'axis' } : { trigger: 'axis', valueFormatter: v => nlNum(Array.isArray(v) ? v[1] : v) },
    legend: {
      data: [inLabel, outLabel, ...(showWx ? [tempLabel] : [])],
      bottom: 0,
    },
    visualMap: showWx ? [{
      show: false,
      type: 'piecewise',
      seriesIndex: 2,
      dimension: 1,
      pieces: [
        { lte: 20, color: '#7fb0d8' },
        { gt: 20, lte: 23, color: '#9fd0cb' },
        { gt: 23, lte: 26, color: '#e6c877' },
        { gt: 26, color: '#e0a94a' },
      ],
    }] : undefined,
    grid: {
      containLabel: true,
      bottom: 30,
      right: showWx ? 46 : undefined,
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { rotate: 45 },
    },
    yAxis: [
      {
        type: 'value',
        name: t('charts.line.crossingsAxis', { span: props.timeSpan }),
        nameTextStyle: { align: 'left' },
        ...(currentLanguage() === 'en' ? {} : { axisLabel: { formatter: nlNum } }),
      },
      ...(showWx ? [{
        type: 'value',
        name: '°C',
        position: 'right',
        min: Math.floor(tMin - 2),
        max: Math.ceil(tMax + 2),
        axisLabel: { formatter: '{value}°', color: '#b0863a' },
        nameTextStyle: { color: '#b0863a' },
        splitLine: { show: false },
      }] : []),
    ],
    series: [
      {
        name: inLabel,
        data: inCounts,
        markLine: markLineFor(props.markers, dates),
        type: 'line',
        smooth: true,
        lineStyle: { width: 3 },
        itemStyle: { color: '#4F7EB3' },
      },
      {
        name: outLabel,
        data: outCounts,
        type: 'line',
        smooth: true,
        lineStyle: { width: 3 },
        itemStyle: { color: '#B2C149' },
      },
      ...(showWx ? [{
        name: tempLabel,
        data: temps.map((t, i) => [i, t]),
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        symbol: 'none',
        connectNulls: true,
        z: 3,
        lineStyle: { width: 2, type: 'dashed' },
      }] : []),
    ],
  }
})

onMounted(() => {
  setTimeout(() => {
    chartRef.value?.resize()
  }, 0)
})
</script>

<style scoped>
.chart-container {
  width: 100%;
  height: 100%;
}
</style>
